// How a value becomes the text on a screen.

import type { BarrierOwner, BarrierViewRow } from './types';

/** Dates arrive as ISO strings and are shown short, in the browser's locale.
 *
 *  `created_at` and `updated_at` are `datetimeoffset` and carry a time; the
 *  eight business dates are `date` and do not. A `date` parsed as an instant
 *  and rendered in a western-hemisphere zone lands on the previous day, which
 *  is why a bare `YYYY-MM-DD` is split rather than passed to `Date`. */
export function formatDate(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const text = String(value);
  const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (bare) {
    const [, year, month, day] = bare;
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString();
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? text : parsed.toLocaleString();
}

/** The form control wants `YYYY-MM-DD`, and so does the API. */
export function dateInputValue(value: unknown): string {
  if (!value) return '';
  const text = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : '';
}

export function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function formatNumber(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  return Number.isNaN(number) ? String(value) : number.toLocaleString();
}

export function formatBool(value: unknown): string {
  if (value === null || value === undefined) return '';
  return value ? 'Yes' : 'No';
}

/** The label the source called `Action Category`.
 *
 *  The source held one string enum whose 23 in-use values encoded a type and up
 *  to two sites -- `IPT - Harris to Philipp`, `Danner Make`. The schema
 *  decomposes it into `action_type`, `origin_site_id` and `dest_site_id`, and
 *  the API groups by the triple, because `action_category_legacy` is null on
 *  every row created since the load: a screen grouped by it would show the
 *  estate freezing on the day of the migration.
 *
 *  So the label is composed back from the triple. It reads the way the source's
 *  values read, and it extends to combinations the source never held. */
export function actionCategory(parts: {
  action_type: string | null;
  origin_site_name: string | null;
  dest_site_name: string | null;
}): string {
  const { action_type: type, origin_site_name: from, dest_site_name: to } = parts;
  if (!type) return 'No action category';
  switch (type) {
    case 'IPT':
      return from && to ? `IPT - ${from} to ${to}` : 'IPT';
    case 'MAKE':
      // The plant that makes it is the To Plant: `CK_barrier_action_sites`
      // refuses a From Plant on MAKE, and the load put `Danner Make`'s plant there.
      return to ? `${to} Make` : 'Make';
    case 'WH_DELIVER':
      return to ? `WH Deliver ${to}` : 'WH Deliver';
    case 'SUPPLIER_EXPEDITE':
      return 'Supplier Expedite';
    case 'ENGINEERING_CHANGE':
      return 'Engineering Change';
    case 'PO_NEEDED':
      return 'PO Needed';
    default:
      return type;
  }
}

/** One owner's name: the directory's where it resolved, the source's where it
 *  did not, and both are ordinary rather than exceptional. */
export function ownerName(owner: BarrierOwner): string {
  return owner.display_name ?? owner.legacy_name ?? '(unnamed)';
}

export function ownerNames(row: BarrierViewRow): string {
  return (row.owners ?? []).map(ownerName).join(', ');
}

/** Who raised a barrier, which the business asked for and the source never had.
 *
 *  The API writes `entered_by_name` from the signed-in user's token on insert.
 *  It is null in two cases, and they read differently:
 *
 *  - A migrated barrier -- `legacy_row_id` set -- is `Legacy`. Nobody recorded
 *    who typed it into the source, and its `created_by` names whoever ran the
 *    load, so falling back to that would name the wrong person.
 *  - A barrier raised here before the API recorded the name has only its
 *    `created_by`, which is the sign-in name of the person who raised it. */
export function enteredBy(row: { entered_by_name?: unknown; legacy_row_id?: unknown; created_by?: unknown }): string {
  if (row.entered_by_name) return String(row.entered_by_name);
  if (row.legacy_row_id) return 'Legacy';
  return row.created_by ? String(row.created_by) : '';
}

/** Who acts on a barrier.
 *
 *  A name with an object id beside it is a person the directory named. A name
 *  without one was typed by hand before Action Owner came from the directory;
 *  it says so, so that it reads as something to correct rather than as a
 *  person anybody can filter on. */
export function actionOwner(row: { action_owner?: unknown; action_owner_object_id?: unknown }): string {
  if (!row.action_owner) return '';
  const name = String(row.action_owner);
  return row.action_owner_object_id ? name : `${name} (not matched)`;
}

/** What a group header says.
 *
 *  An empty one says so rather than rendering blank, which reads as a rendering
 *  fault -- and these groups are not rare: `Owner Department` was blank on 241
 *  barriers of 1,824, `Product` on 88, `Commodity` on 85.
 *
 *  A populated one is formatted the way the column's cells are. The header and
 *  the rows beneath it show one value, so showing it two ways is a defect: the
 *  Two Week Production View grouped weeks as `2026-06-29` over rows reading
 *  `6/29/2026`. */
export function groupLabel(value: unknown, noun: string, type?: 'int' | 'str' | 'date' | 'bool' | 'uuid'): string {
  if (value === null || value === undefined || value === '') return `No ${noun}`;
  if (type === 'date') return formatDate(value);
  if (type === 'int') return formatNumber(value);
  if (type === 'bool') return formatBool(value);
  return String(value);
}
